import { judgeDocumentFailure } from "./judgeDocumentFailure";
import type { BucketClassification, ConnectionCheckId, ConnectionCheckResult, DocumentLoadFailure } from "./types";

type BlockedCheckId = Exclude<ConnectionCheckId, "internet" | "document">;

const friendlyBlocked: Record<BlockedCheckId, string> = {
  cdn: "Entrilia's content delivery service",
  api: "Entrilia's portal service",
  b2c: "Microsoft sign-in",
  blob: "Entrilia's document storage",
};

const hostsBlocked: Record<BlockedCheckId, string[]> = {
  cdn: ["cdn.entrilia.com"],
  api: [],
  b2c: ["*.b2clogin.com", "login.microsoftonline.com"],
  blob: [],
};

// Informational only — shown for IT to allow; the probes don't validate against all of them.
const documentStorageHosts = [
  "safileopsprod001.blob.core.windows.net",
  "safileopsprod001nl.blob.core.windows.net",
  "sabcdataprod001.blob.core.windows.net",
  "sabcdataprod001nl.blob.core.windows.net",
  "sadatacatalogueprod001.blob.core.windows.net",
  "sadatacatalogueprod001nl.blob.core.windows.net",
];

const findResult = (
  checks: ConnectionCheckResult[],
  id: ConnectionCheckResult["id"]
): ConnectionCheckResult | undefined => checks.find((c) => c.id === id);

const findResults = (checks: ConnectionCheckResult[], id: ConnectionCheckResult["id"]): ConnectionCheckResult[] =>
  checks.filter((c) => c.id === id);

const formatList = (items: string[]): string => {
  if (items.length === 0) {
    return "";
  }
  if (items.length === 1) {
    return items[0] ?? "";
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

export type AccessCheckContext = "sign-in" | "document";

interface ClassifyArgs {
  checks: ConnectionCheckResult[];
  hasAccess?: boolean;
  email?: string;
  context?: AccessCheckContext;
  documentLoadError?: DocumentLoadFailure;
}

export const classifyAccessCheckResult = ({
  checks,
  hasAccess,
  email,
  context = "sign-in",
  documentLoadError,
}: ClassifyArgs): BucketClassification => {
  const c1 = findResult(checks, "internet");
  const c2 = findResult(checks, "cdn");
  const c3 = findResult(checks, "api");
  const c4 = findResult(checks, "b2c");

  if (!c1?.ok) {
    return {
      bucket: 2,
      title: "Your device looks offline",
      suggestion:
        "Your device doesn't seem to be connected to the internet. Please check that your Wi-Fi is connected, or that you have mobile data turned on, and try again.",
      bullets: [],
      blockedFriendly: [],
      blockedHosts: [],
    };
  }

  const failingBlobs = findResults(checks, "blob").filter((b) => !b.ok);
  const documentCheck = findResult(checks, "document");
  const failedDocumentCheck = documentCheck && !documentCheck.ok ? documentCheck : undefined;

  const blockedKeys: BlockedCheckId[] = [];
  if (c2 && !c2.ok) {
    blockedKeys.push("cdn");
  }
  if (c3 && !c3.ok) {
    blockedKeys.push("api");
  }
  if (c4 && !c4.ok) {
    blockedKeys.push("b2c");
  }
  if (failingBlobs.length > 0) {
    blockedKeys.push("blob");
  }

  if (blockedKeys.length > 0) {
    const friendly = blockedKeys.map((k) => friendlyBlocked[k]);
    const apiHost = c3?.targetHost;
    const blobHosts = failingBlobs.map((b) => b.targetHost);
    const hosts = blockedKeys.flatMap((k) => {
      if (k === "api" && apiHost) {
        return [apiHost];
      }
      if (k === "blob") {
        return blobHosts;
      }
      return hostsBlocked[k];
    });
    // The failed document's host is the exact storage account IT needs to allow.
    if (failedDocumentCheck && !hosts.includes(failedDocumentCheck.targetHost)) {
      hosts.push(failedDocumentCheck.targetHost);
    }
    const closingBullet =
      context === "document"
        ? "If the document still won't open after switching to another network, please contact your fund manager and share the file below."
        : "If sign-in still fails after switching to another network, please contact your fund manager and share the file below.";
    return {
      bucket: 3,
      title: "Your network seems to block parts of the portal",
      conclusion: `Your network appears to be blocking ${formatList(friendly)}.`,
      suggestion: "This is most likely a firewall restriction set up by your company's IT team.",
      bullets: [
        "Connect your computer, tablet, or phone to a different internet connection — for example, your home Wi-Fi.",
        "Or use your mobile internet (cellular / 4G / 5G data) instead of Wi-Fi.",
        `If you can't switch networks, ask your IT team to allow access to: ${hosts.join(", ")}.`,
        closingBullet,
      ],
      blockedFriendly: friendly,
      blockedHosts: hosts,
    };
  }

  // Azure-authored or unclear falls through to bucket 1, which points at the fund manager.
  if (judgeDocumentFailure(documentLoadError, documentCheck) === "network-blocked") {
    return {
      bucket: 5,
      title: "Your company's network blocked this document",
      conclusion: "Your company's security software is blocking this file.",
      suggestion:
        "Entrilia and its document storage are reachable from this device, so the block is happening on your company's network.",
      bullets: [
        `Please send these addresses to your IT team and ask them to allow file downloads from:\n${documentStorageHosts.join("\n")}`,
        "In the meantime, you can try a different internet connection — for example, your home Wi-Fi or mobile data.",
      ],
      blockedFriendly: ["this document on Entrilia's document storage"],
      blockedHosts: [...documentStorageHosts],
    };
  }

  if (hasAccess === false) {
    const target = email || "this email";
    return {
      bucket: 4,
      title: "We couldn't find your access",
      suggestion: `We don't see Investor Portal access for ${target}. Please contact your fund manager to confirm your access has been set up.`,
      bullets: [],
      blockedFriendly: [],
      blockedHosts: [],
    };
  }

  const reachableSuggestion =
    context === "document"
      ? "Entrilia document storage looks reachable from this device, so the problem may be specific to this document. Please contact your fund manager and share the file below."
      : "Everything looks reachable from your device. Please try signing in again. If sign-in still doesn't work, please contact your fund manager and share the file below.";

  return {
    bucket: 1,
    title: "Everything looks reachable",
    suggestion: reachableSuggestion,
    bullets: [],
    blockedFriendly: [],
    blockedHosts: [],
  };
};
